import axios from "axios";
import { inject, injectable } from "inversify";
import type { IDeploymentConfigurationRepository } from "@domain/repositories";
import type { PathTypeSchema, PathVisibilitySchema, SoftwareEnumSchema, SoftwareVersionContextConfiguration, StorageContextConfiguration } from "@domain/schemas/BI";
import { API_ENDPOINTS } from "@infrastructure/InfrastructureConstants";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { TYPES } from "@utils/inversifyJS/types";
import type { IAuthenticatedRequestHandlerService } from "@application/contracts/services";

@injectable()
export class DeploymentConfigurationRepository implements IDeploymentConfigurationRepository {
  @inject(TYPES.IAuthenticatedRequestHandlerService)
  private _requestHandlerService!: IAuthenticatedRequestHandlerService;

  public async getStorageConfigurationAsync(): Promise<StorageContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.deploymentStorageConfiguration}`;
    return this._requestHandlerService.handleRequestAsync<StorageContextConfiguration>(() => axios.get<StorageContextConfiguration>(url));
  }

  public async addStoragePathAsync(pathType: PathTypeSchema, path: string, visibility: PathVisibilitySchema): Promise<StorageContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.deploymentStorageConfigurationPaths}`;
    return this._requestHandlerService.handleRequestAsync<StorageContextConfiguration>(() => axios.post<StorageContextConfiguration>(url, { pathType, path, visibility }));
  }

  public async removeStoragePathAsync(pathId: string): Promise<StorageContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.deploymentStorageConfigurationPathById}`.replace("{pathId}", () => pathId);
    return this._requestHandlerService.handleRequestAsync<StorageContextConfiguration>(() => axios.delete<StorageContextConfiguration>(url));
  }

  public async setStoragePathValueAsync(pathId: string, path: string): Promise<StorageContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.deploymentStorageConfigurationPathValue}`.replace("{pathId}", () => pathId);
    return this._requestHandlerService.handleRequestAsync<StorageContextConfiguration>(() => axios.put<StorageContextConfiguration>(url, { path }));
  }

  public async setStorageResultsPathAsync(resultsPath: string): Promise<StorageContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.deploymentStorageConfigurationResultsPath}`;
    return this._requestHandlerService.handleRequestAsync<StorageContextConfiguration>(() => axios.put<StorageContextConfiguration>(url, { resultsPath }));
  }

  public async getSoftwareVersionConfigurationAsync(): Promise<SoftwareVersionContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.deploymentSoftwareVersionConfiguration}`;
    return this._requestHandlerService.handleRequestAsync<SoftwareVersionContextConfiguration>(() => axios.get<SoftwareVersionContextConfiguration>(url));
  }

  public async addSoftwareVersionAsync(software: SoftwareEnumSchema, version: string): Promise<SoftwareVersionContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.deploymentSoftwareVersionConfigurationVersions}`;
    return this._requestHandlerService.handleRequestAsync<SoftwareVersionContextConfiguration>(() => axios.post<SoftwareVersionContextConfiguration>(url, { software, version }));
  }

  public async removeSoftwareVersionAsync(versionId: string): Promise<SoftwareVersionContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.deploymentSoftwareVersionConfigurationVersionById}`.replace("{versionId}", () => versionId);
    return this._requestHandlerService.handleRequestAsync<SoftwareVersionContextConfiguration>(() => axios.delete<SoftwareVersionContextConfiguration>(url));
  }

  public async setSoftwareVersionDefaultAsync(software: SoftwareEnumSchema, version: string): Promise<SoftwareVersionContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.deploymentSoftwareVersionConfigurationDefault}`;
    return this._requestHandlerService.handleRequestAsync<SoftwareVersionContextConfiguration>(() => axios.put<SoftwareVersionContextConfiguration>(url, { software, version }));
  }

  public async clearSoftwareVersionDefaultAsync(software: SoftwareEnumSchema): Promise<SoftwareVersionContextConfiguration> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.deploymentSoftwareVersionConfigurationDefaultForSoftware}`.replace("{software}", () => software);
    return this._requestHandlerService.handleRequestAsync<SoftwareVersionContextConfiguration>(() => axios.delete<SoftwareVersionContextConfiguration>(url));
  }
}
